import { useCallback, useState } from 'react';
import { defaultTo, isNil } from 'lodash';

import type { DialogState } from '#packages/web/types/dialog-state';

export const useDialogControl = ({
  state = 'closed',
}: {
  state?: DialogState;
}) => {
  const [dialogState, setDialogState] = useState<DialogState>(
    defaultTo(state, 'closed'),
  );

  const openCallback = useCallback(() => setDialogState('open'), []);
  const closeCallback = useCallback(() => setDialogState('closed'), []);

  return {
    state: dialogState,

    isOpen: dialogState === 'open',
    isClosed: dialogState === 'closed',

    open: openCallback,
    close: closeCallback,
  };
};

export const useDialog = function <TData = unknown>(
  {
    state,
    initialData,
  }: {
    state?: DialogState;
    initialData?: TData;
  } = { state: 'closed' },
) {
  const dialogControl = useDialogControl({ state });
  const [dialogData, setDialogData] = useState<TData | undefined>(initialData);

  const open = useCallback(
    (data?: TData) => {
      if (!isNil(data)) {
        setDialogData(data);
      }
      dialogControl.open();
    },
    [dialogControl.open],
  );

  const close = useCallback(
    /**
     * Closes the dialog and optionally resets its stored data.
     *
     * @param reset - If true, reset the dialog data when closing. Defaults to true.
     * @default true
     */
    (reset: boolean = true) => {
      if (reset === true) {
        setDialogData(undefined);
      }

      dialogControl.close();
    },
    [dialogControl.close],
  );

  return {
    ...dialogControl,

    open,
    close,
    data: dialogData,
  };
};
